import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BrandAwareCard as Card, BrandAwareCardContent as CardContent, BrandAwareCardHeader as CardHeader, BrandAwareCardTitle as CardTitle } from '@/components/brand/BrandAwareCard'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useBrandAware } from '@/hooks/useBrandAware'
import { MetricCard } from './MetricCard'
import { SystemHealthIndicator } from './SystemHealthIndicator'
import { QuickActions } from './QuickActions'
import { ApiKeyForm } from './ApiKeyForm'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useSmartleadSyncStatus } from '@/hooks/useSmartleadSyncStatus'

import { useClients } from '@/hooks/useClients'

import { smartleadAPI } from '@/lib/smartlead-api'
import { Users, Target, Mail, TrendingUp, CheckCircle, AlertTriangle, Calendar, Activity, BarChart3, MessageSquare, Zap } from 'lucide-react'
import { EmailsSentLiveStream } from './EmailsSentLiveStream'
import { LiveActivityFeed } from '@/components/client-reporting/LiveActivityFeed'
import { WebhookMonitor } from './WebhookMonitor'


export function DashboardContent() {
  const navigate = useNavigate()
  const dashboardData = useDashboardData()
  const { data: syncStatus, isLoading: syncLoading } = useSmartleadSyncStatus()
  const { data: clients, isLoading: clientsLoading } = useClients()
  const { getBrandProps, getBrandStyles } = useBrandAware()
  const layoutStyles = getBrandStyles('layout')

  // Calculate active clients count (excluding system 'default' client)
  const filteredClients = clients?.filter(client => client.id !== 'default') || []
  const activeClientsCount = filteredClients.filter(client => client.status === 'Active').length

  // Force refresh when API key becomes configured
  useEffect(() => {
    if (smartleadAPI.isConfigured() && dashboardData.error?.includes('demo data')) {
      console.log('🔄 API key is now configured, refreshing dashboard data...')
      dashboardData.refresh()
    }
  }, [smartleadAPI.isConfigured()])

  const isConfigured = smartleadAPI.isConfigured()
  const showDemoWarning = !isConfigured || dashboardData.error?.includes('demo data')
  
  console.log('🔑 Dashboard API Key Form Debug:', {
    isConfigured,
    showDemoWarning,
    hasError: !!dashboardData.error
  })

  return (
    <div className="space-y-6">
      {/* ApiKeyForm hidden - API key configured in environment variable */}
      {/* Uncomment for development/testing: <ApiKeyForm /> */}
      

      {isConfigured && showDemoWarning && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            API key configured but data is still loading. Please wait while we fetch your Smartlead data...
          </AlertDescription>
        </Alert>
      )}

      {dashboardData.error && !showDemoWarning && (
        <Alert className={dashboardData.usingCache ? "border-yellow-200 bg-yellow-50" : "border-red-200 bg-red-50"}>
          <AlertTriangle className={`h-4 w-4 ${dashboardData.usingCache ? "text-yellow-600" : "text-red-600"}`} />
          <AlertDescription className={dashboardData.usingCache ? "text-yellow-800" : "text-red-800"}>
            {dashboardData.error}
          </AlertDescription>
        </Alert>
      )}

      {(dashboardData.loading || dashboardData.mailboxesLoading) && dashboardData.loadingStatus && (
        <Alert className="border-blue-200 bg-blue-50">
          <Activity className="h-4 w-4 text-blue-600 animate-pulse" />
          <AlertDescription className="text-blue-800">
            {dashboardData.loadingStatus}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-retro-star text-2xl font-bold text-midnight mb-2">
            Dashboard Overview
          </h2>
          <p className="text-midnight/70 font-sora">
            Your comprehensive Smartlead metrics and system status.
          </p>
        </div>
        <SystemHealthIndicator 
          lastUpdated={dashboardData.lastUpdated}
          error={dashboardData.error}
          usingCache={dashboardData.usingCache}
        />
      </div>

      {/* Main Dashboard Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Clients"
          value={activeClientsCount}
          loading={clientsLoading}
          icon={<Users className="h-4 w-4" />}
          onClick={() => navigate('/clients')}
        />
        <MetricCard
          title="Active Campaigns"
          value={dashboardData.activeCampaigns}
          loading={dashboardData.loading}
          icon={<Target className="h-4 w-4" />}
          onClick={() => navigate('/campaign-performance-test')}
        />
        <MetricCard
          title="Active Triggers"
          value={dashboardData.activeTriggers}
          loading={dashboardData.loading}
          icon={<Zap className="h-4 w-4" />}
          onClick={() => navigate('/campaign-performance-test')}
        />
        <MetricCard
          title="Active Variants"
          value={dashboardData.activeVariants}
          loading={dashboardData.loading}
          icon={<BarChart3 className="h-4 w-4" />}
          onClick={() => navigate('/campaign-performance-test')}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Today's Emails"
          value={dashboardData.todaysEmails}
          loading={dashboardData.loading}
          icon={<TrendingUp className="h-4 w-4" />}
          onClick={() => navigate('/campaigns')}
        />
        <MetricCard
          title="Overall Reply Rate"
          value={`${dashboardData.overallReplyRate}%`}
          loading={dashboardData.loading}
          icon={<MessageSquare className="h-4 w-4" />}
          onClick={() => navigate('/campaigns')}
        />
        <MetricCard
          title="Active Domains"
          value={dashboardData.activeDomains}
          loading={dashboardData.loading}
          icon={<CheckCircle className="h-4 w-4" />}
          onClick={() => navigate('/domains-emails')}
        />
        <MetricCard
          title="Active Mailboxes"
          value={dashboardData.activeMailboxes}
          loading={dashboardData.loading}
          icon={<Mail className="h-4 w-4" />}
          onClick={() => navigate('/domains-emails?section=mailboxes')}
        />
      </div>


      {/* Live Activity Feed */}
      <LiveActivityFeed campaigns={[]} loading={dashboardData.loading} />

      {/* Real-Time Email Activity */}
      <EmailsSentLiveStream />

      {/* Webhook Monitor */}
      <WebhookMonitor />

      <QuickActions 
        onRefresh={dashboardData.refresh}
        refreshing={dashboardData.loading}
      />
    </div>
  )
}
